import type { ReactElement, ReactNode } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface ReadOnlyFieldValueProps {
	children: ReactNode;
	className?: string;
}

/**
 * A read-only, input-styled display of a value the user cannot change in the
 * current context — e.g. a warehouse field hard-scoped to the user's active
 * warehouse (PD-13742). It looks like a disabled input (muted background) so the
 * value reads as "set, not editable" rather than missing, which avoids the
 * confusion of simply hiding the field.
 *
 * Drop it inside a FormField in place of the normal selector; any cloned
 * `id` / `aria-*` / `size` props FormField injects are harmlessly ignored.
 */
export function ReadOnlyFieldValue({
	children,
	className,
}: ReadOnlyFieldValueProps): ReactElement {
	const colors = useThemeColors();

	return (
		<div
			className={cn(
				'w-full rounded-md border px-3 py-2 text-sm',
				colors.classes.bgSecondary,
				colors.classes.borderDefault,
				colors.classes.textSecondary,
				className,
			)}
		>
			{children}
		</div>
	);
}
